//#region IMPORTS
import { fromBackendDate } from "../../helpers/fromBackendDate";
import { useAppSelector } from "../../store/configureStore";
import { InstoreOrder } from "../../models/instoreOrder";
import { downloadFile } from "../../helpers/downloadFile";
import { useHistory } from "react-router-dom";
import { Fragment } from "react";
import { Tooltip } from "react-tooltip";
import { format } from "date-fns";
import { toast } from "react-toastify";
import InstoreOrdersTableRowTrackAndTraceMenu from "./InstoreOrdersTableRowTrackAndTraceMenu";
import InstoreOrdersTableRowActionEdit from "./InstoreOrdersTableRowActionEdit";
import MainTableRowActionDelete from "../_layout/MainTableRowActionDelete";
import MainTableRowActionRead from "../_layout/MainTableRowActionRead";
import OrderStatusSelectBox from "../orders/OrderStatusSelectBox";
import OrderStatusBadge from "../orders/OrderStatusBadge";
import OrderTypeBadge from "../orders/OrderTypeBadge";
import classNames from "../../helpers/classNames";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  changeOrderStatusHandler: (
    instoreOrder: InstoreOrder,
    status: string
  ) => void;
  deleteHandler: () => void;
  instoreOrder: InstoreOrder;
  isAdmin?: boolean;
}
//#endregion

export default function InstoreOrdersTableRow({
  changeOrderStatusHandler,
  deleteHandler,
  instoreOrder,
  isAdmin = false,
}: Props) {
  //#region SETUP
  // Router
  const history = useHistory();

  // Redux
  const { filters } = useAppSelector((state) => state.orders);
  //#endregion

  //#region LOGIC
  const exportInstoreOrderHandler = async () => {
    try {
      let blobFromFetch: Blob;
      const fileName = `InstoreOrder__${instoreOrder.order_code}__${
        instoreOrder.store_name
      }__${format(new Date(), "dd-MM-yyyy")}`;

      const response = await toast.promise(
        agent.InstoreOrders.export(instoreOrder.id),
        {
          pending: "Generating export data",
          success: "Download succeeded",
          error: "Something went wrong",
        }
      );

      blobFromFetch = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      downloadFile({
        blobFromFetch,
        extension: "xlsx",
        fileName,
      });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unexpected error while downloading";
      toast.error(message);
    }
  };

  const detailHandler = () => {
    history.push(`/instoreorders/${instoreOrder.id}`, true);
  };

  //#endregion

  //#region RENDER
  return (
    <tr className="border-t border-slate-300 h-10">
      {/* read */}
      <td className="whitespace-nowrap px-2 text-left text-xs flex items-center h-10 gap-2 text-gray-500 sm:pl-0">
        <MainTableRowActionRead detailHandler={detailHandler} />
        {isAdmin ? (
          <InstoreOrdersTableRowActionEdit instoreOrder={instoreOrder} />
        ) : null}
      </td>

      {/* order_date */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.order_date}
      </td>

      {/* order_code */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.order_code}
      </td>

      {/* order_type_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        <OrderTypeBadge orderTypeName={instoreOrder.order_type_instore_name} />
      </td>

      {/* order_status_name */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {/* {isAdmin && instoreOrder.order_status_name !== ORDERSTATUS.shipped ? ( */}
        {isAdmin ? (
          <OrderStatusSelectBox
            currentStatus={instoreOrder.order_status_name}
            orderStatusses={filters.order_status}
            changeOrderStatusHandler={(status: string) =>
              changeOrderStatusHandler(instoreOrder, status)
            }
          />
        ) : (
          <OrderStatusBadge value={instoreOrder.order_status_name} />
        )}
      </td>

      {/* external_store_id */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.external_store_id ?? "Not found"}
      </td>

      {/* store_language */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.store_language}
      </td>

      {/* delivery_date */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {fromBackendDate(instoreOrder.delivery_date)}
      </td>

      {/* store_opening_date */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {fromBackendDate(instoreOrder.store_opening_date)}
      </td>

      {/* article_amount */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0">
        {instoreOrder.total_amount}
      </td>

      {/* comment */}
      <td className="whitespace-nowrap px-2 text-left text-xs sm:pl-0">
        <div
          data-tooltip-content={
            instoreOrder.comment && instoreOrder.comment !== ""
              ? instoreOrder.comment.split(";").join(" - ")
              : null
          }
          data-tooltip-variant="info"
          data-tooltip-place="left"
          data-tooltip-id={instoreOrder.order_code + "extraprojects"}
          className={classNames(
            instoreOrder.comment && instoreOrder.comment !== ""
              ? "font-bold text-gray-900"
              : "text-gray-500"
          )}
        >
          {instoreOrder.comment && instoreOrder.comment !== ""
            ? `${instoreOrder.comment?.split(";").length} project${
                instoreOrder.comment?.split(";").length > 1 ? "s" : ""
              }`
            : "No extra projects"}
          {instoreOrder.comment && instoreOrder.comment !== "" ? (
            <Tooltip id={instoreOrder.order_code + "extraprojects"} />
          ) : null}
        </div>
      </td>

      {/* track_and_trace */}
      <td className="whitespace-nowrap px-2 text-left text-xs text-gray-500 sm:pl-0 py-2 ">
        {instoreOrder.track_and_trace ? (
          <InstoreOrdersTableRowTrackAndTraceMenu
            parcels={instoreOrder.track_and_trace.split(";")}
          />
        ) : (
          <span className="py-1 text-sm text-gray-600">
            no tracking info available
          </span>
        )}
      </td>

      {/* export */}
      {isAdmin ? (
        <Fragment>
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            <button
              className="rounded-full bg-sky-900 px-2 py-1 text-xs font-bold text-white hover:bg-sky-700"
              onClick={exportInstoreOrderHandler}
            >
              EXPORT
            </button>
          </td>

          {/* delete */}
          <td className="whitespace-nowrap px-2 text-left text-xs text-gray-900">
            <MainTableRowActionDelete
              deleteHandler={deleteHandler}
              tooltipId={`instoreOrder${instoreOrder.id}`}
            />
          </td>
        </Fragment>
      ) : null}
    </tr>
  );
  //#endregion
}
